import { io } from "socket.io-client";

// Instancia singleton del socket — un único socket por sesión de usuario.
// Todos los módulos que necesiten el socket deben usar getSocket() o connectToServer().
let socket;

// Adjunta los listeners de sesión al socket dado.
// Se extrae para poder re-adjuntarlos limpiamente en cada socket fresco.
function attachSessionListeners(sock) {
  sock.on('session-terminated', (data) => {
    let message = 'Tu sesión ha sido cerrada';
    if (data.reason === 'duplicate_session') {
      message = 'Tu sesión se cerró porque iniciaste sesión en otro dispositivo';
    } else if (data.reason === 'admin_disconnect') {
      message = 'Tu sesión fue cerrada por un administrador';
    }
    window.dispatchEvent(new CustomEvent('force-logout', {
      detail: {
        reason: data.reason,
        message: data.message || message,
      },
    }));
  });

  sock.on('disconnect', (reason) => {
    console.warn('⚠️ WebSocket desconectado. Razón:', reason);
    if (reason === 'io server disconnect') {
      window.dispatchEvent(new CustomEvent('force-logout', {
        detail: { reason: 'server_forced_disconnect' },
      }));
    }
  });
}

export const connectToServer = (token, isNewLogin = false) => {
  return new Promise((resolve, reject) => {

    // ── Caso 1: socket activo y conectado ──────────────────────────────────
    // Reutilizar sin crear una nueva instancia ni duplicar listeners.
    if (socket && socket.connected) {
      resolve(socket);
      return;
    }

    // ── Caso 2: socket existe pero desconectado ────────────────────────────
    // Solo se llega aquí durante reconexiones automáticas por pérdida de red
    // (ping timeout, etc.) — NO durante login, porque disconnectSocket() en el
    // logout pone socket = null, forzando el Caso 3 en el próximo login.
    // isNewLogin ya fue reseteado a false tras el primer connect, así que el
    // backend no interpretará esta reconexión como un nuevo login.
    if (socket && !socket.connected) {
      const onConnect = () => {
        socket.off('connect_error', onError);
        resolve(socket);
      };
      const onError = (err) => {
        socket.off('connect', onConnect);
        console.error('❌ Error al reconectar WebSocket:', err.message);
        reject(err);
      };
      socket.once('connect', onConnect);
      socket.once('connect_error', onError);
      socket.connect();
      return;
    }

    // ── Caso 3: sin socket — primera conexión (o tras disconnectSocket) ───
    const SOCKET_URL = process.env.REACT_APP_BASE_URL_SOCKET;

    socket = io(SOCKET_URL, {
      path: "/socket.io",
      // Solo websocket: evita la negociación polling→websocket, que puede
      // perder mensajes emitidos por el backend en ráfaga (varios eventos
      // pegados en el mismo instante) justo mientras el transporte está
      // en medio de esa negociación.
      transports: ["websocket"],
      auth: { token, isNewLogin },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      pingInterval: 10000,
      pingTimeout: 5000,
    });

    socket.on('connect', () => {
      // Resetear isNewLogin tras el primer connect para que las reconexiones
      // automáticas (ping timeout, red inestable) no disparen checkUserConnection
      // en el backend, lo que terminaría la sesión activa.
      socket.auth = { token, isNewLogin: false };
      resolve(socket);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Error de conexión WebSocket:', error.message);
      reject(error);
    });

    attachSessionListeners(socket);
  });
};

// Desconecta el socket limpiando todos los listeners y la referencia singleton.
// Debe llamarse en logout para garantizar que el próximo connectToServer cree un
// socket fresco — sin listeners residuales que puedan disparar force-logout
// en la nueva sesión cuando el backend termina la sesión anterior.
export const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
};

export const getSocket = () => socket;
